/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.com/docs/browser-apis/
 */
const posthog = require("posthog-js").default
const Intercom = require("@intercom/messenger-js-sdk").default
const { targetBlank } = require("./src/utils/urls")
const { analyticsAction, allowCapture } = require("./src/utils/analytics")
const {
  getCookie,
  updateUtmCookie,
  updateCtaCookie,
  readAttributionQuery,
  registerPostHogAttribution,
  toIntercomLanguage,
} = require("@filmustage/shared")
const { onLoad: onDefenderLoad, onError: onDefenderError } = require("@dataunlocker/defender")

// Same Intercom workspace as the logged-in app (apps/frontend) so the
// same `intercom-id-ke6a59w1` cookie on `.filmustage.com` stitches the
// visitor session on landing to the user session in-app. A visitor who
// chats on the landing page sees the same conversation history after
// they log in. App ID is public (workspace identifier, no secret value).
const INTERCOM_APP_ID = "ke6a59w1"

const {
  gallery,
  video,
  audio,
  toggle,
  beforeAfter,
} = require("./src/utils/ghost")

exports.onClientEntry = () => {
  updateUtmCookie()
  // Separate cookie, separate meaning: acquisition above, which internal CTA
  // the visitor came through here. Landing pages carry fm_page/fm_cta when the
  // visitor arrives from the app (e.g. the in-app pricing modal).
  updateCtaCookie()

  // Init PostHog with capture held until Defender confirms its proxy is up,
  // mirroring frontend's pattern (apps/frontend/src/index.js#L50). Without the
  // gate, the first events of a cold-start session race the handshake and may
  // slip past ad-blockers. Key + host are public (key is a project-level
  // write-only id, host is the DataUnlocker-fronted CNAME `fox.filmustage.com`).
  // Landing is one public deployment shared across environments, so it always
  // sends to the Prod project (Filmustage App, id 228051) — there is no dev
  // landing host needing a split key.
  //
  // `opt_in_site_apps` enables client-side site_destination hog functions
  // (Google Tag Manager dispatcher etc.) - without it destinations stay
  // configured in PostHog but never inject into the page. Required per
  // PostHog docs for the experimental site_destination feature.
  posthog.init("phc_DsBUDakXivUqTwYWeMDA3NJwB9GgGQxcgTOJYtWZ2QK", {
    api_host: "https://fox.filmustage.com",
    ui_host: "https://us.posthog.com", // UI/toolbar/flag-editor host; capture still goes via api_host proxy
    person_profiles: "identified_only",
    capture_pageview: false, // pageviews fired manually from analyticsAction
    opt_in_site_apps: true,
    opt_out_capturing_by_default: true,
  })
  // Expose for src/utils/analytics.js (avoids re-importing posthog-js everywhere).
  window.posthog = posthog

  // Boot Intercom Messenger in anonymous-visitor mode. Once a visitor signs
  // in via the app, frontend's analyticsAction('user_data_set') calls
  // window.Intercom('update', { user_id, email, ... }) and the visitor
  // converts into an identified user on the same Intercom contact - the
  // shared `intercom-id-ke6a59w1` cookie on `.filmustage.com` keys the
  // continuation. `language_override` mirrors the i18n cookie that landing
  // and frontend share (`i18nextLng`), so Belarusian/Ukrainian/Brazilian
  // locales get the correct Messenger UI even before login.
  const lang = toIntercomLanguage(getCookie("i18nextLng"))
  Intercom({
    app_id: INTERCOM_APP_ID,
    ...(lang && { language_override: lang }),
  })

  // Register first-touch + per-event PostHog attribution as soon as PostHog
  // boots so the visitor's *first* pageview already carries initial_utm_* /
  // initial_li_fat_id / initial_landing_page. Without this, the landing
  // visit only had attribution on event.properties (lost to person-level
  // queries). Reads UTM from `The_Boy_Who_Lived` cookie that
  // `updateUtmCookie()` just wrote above. Safe to call before opt_in -
  // register() only mutates client-side persistence.
  registerPostHogAttribution(posthog, readAttributionQuery(), {
    referrer: document.referrer || "",
    url: window.location.href || "",
  })

  // DataUnlocker Defender auto-initialises on import. Failsafe mode -> never
  // blocks rendering on error; analytics keeps flowing without proxy protection.
  onDefenderLoad(() => {
    posthog.opt_in_capturing()
    allowCapture()
  })
  onDefenderError((error) => {
    // Proxy unreachable - still opt in so we keep getting events without
    // ad-block protection, then surface the failure as a tracked event.
    posthog.opt_in_capturing()
    allowCapture()
    console.error("[DataUnlocker] handshake failed:", error)
    posthog.capture("dataunlocker_handshake_failed", {
      message: error?.message || "unknown",
    })
  })

  const hostname = window.location.hostname
  // If not on localhost or 127.0.0.1, override console methods
  if (hostname !== "localhost" && hostname !== "127.0.0.1") {
    console.log = () => {}
    console.debug = () => {}
    console.info = () => {}
    console.warn = () => {}
  }
}

// Outbound-link click delay so PostHog has time to flush the event before
// navigation steals the tab. PostHog auto-uses sendBeacon for in-flight
// captures, but a small wait still helps slower networks.
const LoadAn = async time => {
  await new Promise(resolve => setTimeout(resolve, time))
}

setTimeout(() => {
  analyticsAction("landing_visit")
}, 100)

const isDevEnvironment =
  window.location.hostname.startsWith("landev.filmustage.com") ||
  window.location.hostname.startsWith("localhost")

/**
 * Runs after every route render, the first one included: wires the outbound-link delay, sends the
 * page view, re-reads the page URL in Intercom on client-side navigation, and runs Ghost card
 * scripts.
 *
 * @param {{ prevLocation: Location | null }} args - Gatsby route args; prevLocation is null on the first render.
 */
exports.onRouteUpdate = ({ prevLocation }) => {
  const externalLinks = document.querySelectorAll("a[href^='https']")

  externalLinks.forEach(link => {
    link.addEventListener("click", async e => {
      link.disabled = true
      link.style.pointerEvents = "none"
      link.classList.add("animate-pulse")

      function clearAnimation(time) {
        setTimeout(() => {
          link.disabled = false
          link.style.pointerEvents = "auto"
          link.classList.remove("animate-pulse")
        }, time)
      }

      if (isDevEnvironment && link.href.includes("app.filmustage.com")) {
        link.href = link.href.replace(
          "app.filmustage.com",
          "dev.filmustage.com",
        )
      }

      if (link.getAttribute("target") === "_blank") {
        await LoadAn(0)
        clearAnimation(0)
      } else {
        e.preventDefault()
        await LoadAn(500)
        window.location.href = link.getAttribute("href")
        clearAnimation(1500)
      }
    })
  })

  setTimeout(() => {
    analyticsAction("user_load_page")
  }, 100)

  // Intercom only re-reads the page URL on boot and on `update`. Without this, a client-side
  // navigation (home -> /pricing) never matches the URL rules of its posts, e.g. the lifetime deal.
  // The first render has no prevLocation: the boot in onClientEntry already sent this URL.
  if (prevLocation) window.Intercom && window.Intercom("update")

  // Ghost card scripts
  // https://ghost.org/docs/themes/content/
  gallery()
  video()
  audio()
  toggle()
  beforeAfter()
  targetBlank()
}

exports.onInitialClientRender = () => {
  const scriptSrc = isDevEnvironment
    ? "https://assets.sandbox.cello.so/attribution/latest/cello-attribution.js"
    : "https://assets.cello.so/attribution/latest/cello-attribution.js"

  const script = document.createElement("script")
  script.type = "module"
  script.src = scriptSrc
  script.async = true
  document.head.appendChild(script)
}

/*
  https://www.gatsbyjs.com/docs/how-to/performance/add-offline-support-with-a-service-worker/
  https://github.com/gatsbyjs/gatsby/issues/9087#issuecomment-774680408

  exports.onServiceWorkerUpdateReady = () => window.location.reload()
}*/

//import { focusHandling } from "cruip-js-toolkit"
// On change location:
// https://stackoverflow.com/questions/61274365/allow-component-to-detect-route-change-in-gatsby
// https://github.com/gatsbyjs/gatsby/issues/23051#issuecomment-613557479
